import { useAppSelector } from '@/store';
import { SUPER_ROLES, AUTH_STATUS } from '@/features/auth/constants';

/**
 * Hook to check if the current user has a specific permission.
 *
 * - Single string: returns true if user has that exact permission
 * - Array of strings: returns true if user has ANY of them
 * - Super roles (org_owner, org_admin) always return true
 * - Legacy/migrating sessions always return true (backward compat)
 *
 * @example
 * const canEditSurveys = usePermission('surveys.write');
 * const canViewAnalytics = usePermission('analytics.read');
 * const canManageBilling = usePermission(['billing.read', 'billing.write']);
 */
export const usePermission = (permission: string | string[]): boolean => {
    const { roles, permissions } = useAppSelector((state) => state.auth.context);
    const status = useAppSelector((state) => state.auth.status);

    // Legacy sessions (migrating state) bypass all permission checks
    if (status === AUTH_STATUS.MIGRATING) {
        return true;
    }

    // Fallback for fresh signups: if the backend hasn't assigned any roles yet,
    // treat the user as an owner (they just created the account) so they aren't
    // completely blocked from using the app (e.g., hiding the Create Survey button).
    if (roles.length === 0) {
        return true;
    }

    // Super roles bypass all permission checks
    const isSuperRole = roles.some((role) =>
        SUPER_ROLES.includes(role as (typeof SUPER_ROLES)[number])
    );
    if (isSuperRole) {
        return true;
    }

    // Check actual permissions
    if (Array.isArray(permission)) {
        return permission.some((p) => permissions.includes(p));
    }
    return permissions.includes(permission);
};

export default usePermission;
